import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ResourceCard from '../components/ResourceCard';
import { Search, Filter } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Library = () => {
  const { type } = useParams();
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchResources();
  }, [selectedCategory, searchQuery]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchResources = async () => {
    setLoading(true);
    try {
      let url = `${API}/resources?limit=50`;
      if (selectedCategory) url += `&category=${selectedCategory}`;
      if (searchQuery) url += `&search=${searchQuery}`;
      
      const { data } = await axios.get(url);
      setResources(data);
    } catch (error) {
      console.error('Failed to fetch resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    switch (type) {
      case 'story': return 'Story Library';
      case 'audio': return 'Audio Library';
      case 'video': return 'Video Library';
      default: return 'Resource Library';
    }
  };

  const getDescription = () => {
    switch (type) {
      case 'story': return 'Browse our collection of biblical stories and teachings in text format.';
      case 'audio': return 'Listen to audio teachings, sermons, and biblical stories.';
      case 'video': return 'Watch video teachings and visual presentations of biblical stories.';
      default: return 'Explore our complete collection of biblical resources.';
    }
  };

  const getBannerImage = () => {
    switch (type) {
      case 'audio': return 'https://images.unsplash.com/photo-1718382341267-aef8a9e4ecef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxsaXN0ZW5pbmclMjBoZWFkcGhvbmVzJTIwcGVhY2VmdWx8ZW58MHx8fHwxNzgyMzEwMDI0fDA&ixlib=rb-4.1.0&q=85';
      case 'story': return 'https://images.pexels.com/photos/15382621/pexels-photo-15382621.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
      default: return 'https://images.pexels.com/photos/13036981/pexels-photo-13036981.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
    }
  };

  const filteredResources = resources.filter(resource => {
    if (type === 'audio') return resource.mp3_url;
    if (type === 'video') return resource.youtube_url || resource.video_url;
    if (type === 'story') return resource.pdf_url || resource.description;
    return true;
  });

  return (
    <div className="min-h-screen bg-background-primary">
      <Navbar />

      {/* Banner */}
      <section 
        className="relative h-80 flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: `url(${getBannerImage()})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
        data-testid="library-banner"
      >
        <div className="absolute inset-0 bg-brand-primary/80"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h1 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter leading-tight text-white mb-4" data-testid="library-title">
            {getTitle()}
          </h1>
          <p className="font-body text-lg text-white/90 max-w-2xl mx-auto">
            {getDescription()}
          </p>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm"
                data-testid="library-search-input"
              />
            </div>

            {/* Category Filter */}
            <div className="md:w-64">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded focus:border-brand-accent focus:ring-2 focus:ring-brand-accent/20 outline-none font-body text-sm appearance-none bg-white"
                data-testid="library-category-filter"
              >
                <option value="">All Categories</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.name}>{cat.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {loading ? (
            <div className="text-center py-12">
              <p className="font-body text-slate-600">Loading resources...</p>
            </div>
          ) : filteredResources.length === 0 ? (
            <div className="text-center py-12">
              <p className="font-body text-slate-600">No resources found. Try adjusting your filters.</p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <p className="font-body text-sm text-slate-600">
                  Showing {filteredResources.length} {filteredResources.length === 1 ? 'resource' : 'resources'}
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="library-resources-grid">
                {filteredResources.map((resource) => (
                  <ResourceCard key={resource.id} resource={resource} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Library;